import type { TrophyId } from '@crowdfpl/domain/gamification';
import { ChevronDown } from 'lucide-react';
import { type ReactElement, type SyntheticEvent } from 'react';
import { trackUmamiEvent } from '@/helpers/analytics';
import { cn } from '@/helpers/utils';
import { useIsMobile } from '@/hooks/utils/use-mobile';

type TrophyConditionsProps = {
  conditions: readonly string[];
  className?: string;
  trophyId?: TrophyId;
};

/**
 * "How to earn" bullet list in a boxed block (Lovable parity): always open on
 * desktop, a native <details> disclosure (collapsed) on mobile.
 */
export function TrophyConditions({
  conditions,
  className,
  trophyId,
}: TrophyConditionsProps): ReactElement | null {
  const isMobile = useIsMobile();

  const handleToggle = (event: SyntheticEvent<HTMLDetailsElement>): void => {
    if (!trophyId || !event.currentTarget.open) {
      return;
    }
    trackUmamiEvent('gamification_trophy_conditions_open', { trophy_id: trophyId });
  };

  if (conditions.length === 0) {
    return null;
  }

  const label = (
    <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
      How to earn
    </span>
  );
  const items = (
    <ul className="space-y-1 pt-1.5">
      {conditions.map(condition => (
        <li
          key={condition}
          className="flex items-start gap-1.5 text-[11px] leading-snug text-foreground/80"
        >
          <span
            className="mt-[2px] flex h-3 w-3 shrink-0 items-center justify-center text-[10px] leading-none text-muted-foreground"
            aria-hidden
          >
            •
          </span>
          <span>{condition}</span>
        </li>
      ))}
    </ul>
  );
  const wrapperClass = cn('rounded-md border border-border/60 bg-muted/25 px-2.5 py-1.5', className);

  if (!isMobile) {
    return (
      <div className={wrapperClass}>
        {label}
        {items}
      </div>
    );
  }

  return (
    <details
      className={cn(wrapperClass, 'group')}
      data-testid="trophy-conditions-details"
      onToggle={handleToggle}
    >
      <summary className="flex cursor-pointer list-none items-center justify-between [&::-webkit-details-marker]:hidden">
        {label}
        <ChevronDown
          className="h-3.5 w-3.5 text-muted-foreground transition-transform motion-reduce:transition-none group-open:rotate-180"
          aria-hidden
        />
      </summary>
      {items}
    </details>
  );
}
